import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Phone, Mail } from "lucide-react";
import { handleCall, handleEmail, GMAIL_COMPOSE_URL } from "../lib/contact";

const HERO_IMG =
    "https://customer-assets.emergentagent.com/job_783724d6-9715-4541-9cca-454ee00fdf83/artifacts/8powsrpl_IMG_0488.jpeg";
const CAR_IMG =
    "https://images.pexels.com/photos/34404450/pexels-photo-34404450.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STAGE_IMG =
    "https://images.unsplash.com/photo-1669459902434-39a7f6b4ca48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxzdGFnZSUyMHNwb3RsaWdodCUyMHJlZHxlbnwwfHx8fDE3NzY2NTk2ODZ8MA&ixlib=rb-4.1.0&q=85";

const OFFERINGS = [
    { label: "01", title: "New Car", desc: "Luxury. Keys in your hand.", img: CAR_IMG, span: "md:col-span-5 md:row-span-2" },
    { label: "02", title: "Rapper", desc: "Stage. Studio. Spotlight.", img: STAGE_IMG, span: "md:col-span-4" },
    { label: "03", title: "Model", desc: "Camera. Covers. Contracts.", img: null, span: "md:col-span-3" },
    { label: "04", title: "Movie Star", desc: "Big screens. Big names.", img: null, span: "md:col-span-4" },
    { label: "05", title: "CEO", desc: "Your business. My backing.", img: null, span: "md:col-span-3" },
];

const MARQUEE_WORDS = [
    "Loyalty",
    "Trust",
    "Fast Results",
    "6 Months or Less",
    "Financial Backing",
    "Family",
];

const HeroSection = () => (
    <section className="relative min-h-[92vh] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
            <img src={HERO_IMG} alt="Elijah Golden" className="w-full h-full object-cover object-center opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 flex flex-col justify-between min-h-[92vh]">
            <div className="animate-fade-up">
                <p className="text-xs md:text-sm uppercase tracking-[0.4em] text-[#FF1A1A] mb-6 md:mb-10">
                    — Elijah Golden / Benefactor
                </p>
                <h1 className="font-heading uppercase text-[#FF1A1A] leading-[0.82] text-[64px] sm:text-[96px] md:text-[140px] lg:text-[180px] xl:text-[220px]">
                    Tell Me
                    <br />
                    Your
                    <br />
                    Desire
                </h1>
            </div>

            <div className="mt-12 md:mt-0 flex flex-col md:flex-row md:items-end md:justify-between gap-8 max-w-[1400px]">
                <p className="font-body text-lg md:text-xl lg:text-2xl text-zinc-200 max-w-xl leading-relaxed font-light">
                    Whatever you want — a new car, a record deal, a business, a career —
                    <span className="text-[#FF1A1A] font-medium">
                        {" "}I will put my own money on the table{" "}
                    </span>
                    and back your dream until it's real.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                        to="/contact"
                        className="group inline-flex items-center justify-center gap-3 bg-[#FF1A1A] text-black font-heading uppercase tracking-widest px-8 py-5 hover:bg-white transition-colors duration-300"
                        data-testid="hero-cta-contact"
                    >
                        Call Me Now
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                        to="/about"
                        className="inline-flex items-center justify-center gap-3 bg-transparent border-2 border-white text-white font-heading uppercase tracking-widest px-8 py-5 hover:bg-white hover:text-black transition-colors duration-300"
                        data-testid="hero-cta-about"
                    >
                        The Mission
                    </Link>
                </div>
            </div>
        </div>
    </section>
);

const MarqueeBar = () => (
    <section
        className="border-y-2 border-[#FF1A1A] bg-[#FF1A1A] py-5 overflow-hidden"
        data-testid="marquee-section"
    >
        <Marquee speed={60} gradient={false} className="font-heading uppercase text-black text-4xl md:text-6xl tracking-tight">
            {MARQUEE_WORDS.map((w) => (
                <span key={w} className="flex items-center">
                    <span className="mx-8">{w}</span>
                    <span className="mx-8 text-black/40">✦</span>
                </span>
            ))}
        </Marquee>
    </section>
);

const OfferingCard = ({ offering }) => (
    <div
        className={`group relative bg-[#111111] border border-white/10 p-8 md:p-10 min-h-[280px] md:min-h-[340px] flex flex-col justify-between transition-all duration-500 hover:border-[#FF1A1A] hover:-translate-y-2 overflow-hidden ${offering.span}`}
        data-testid={`offering-card-${offering.label}`}
    >
        {offering.img && (
            <>
                <img
                    src={offering.img}
                    alt={offering.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
            </>
        )}
        <div className="relative flex items-start justify-between">
            <span className="font-heading text-xl text-[#FF1A1A]">{offering.label}</span>
            <ArrowRight className="w-6 h-6 text-white/40 group-hover:text-[#FF1A1A] group-hover:translate-x-1 transition-all" />
        </div>
        <div className="relative">
            <h3 className="font-heading uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-none mb-3 group-hover:text-[#FF1A1A] transition-colors">
                {offering.title}
            </h3>
            <p className="text-zinc-400 text-sm md:text-base">{offering.desc}</p>
        </div>
    </div>
);

const OfferingsSection = () => (
    <section className="py-24 md:py-36 px-6 md:px-12" data-testid="offerings-section">
        <div className="max-w-[1600px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-6">— What You Desire</p>
                    <h2 className="font-heading uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
                        Pick Your
                        <br />
                        <span className="text-[#FF1A1A]">Weapon.</span>
                    </h2>
                </div>
                <p className="text-zinc-400 text-lg max-w-md font-light leading-relaxed">
                    These are just starting points. Whatever the goal — I back it with money,
                    resources, and my team. No guessing. No waiting.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6" data-testid="offerings-grid">
                {OFFERINGS.map((o) => (
                    <OfferingCard key={o.label} offering={o} />
                ))}
            </div>
        </div>
    </section>
);

const PromiseSection = () => (
    <section className="relative py-24 md:py-36 px-6 md:px-12 border-y border-white/10" data-testid="promise-section">
        <div className="max-w-[1400px] mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-8">— The Promise</p>
            <h2 className="font-heading uppercase text-white text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
                I actually <span className="text-[#FF1A1A]">want</span> to see you
                <br />
                succeed. <span className="text-stroke-white">Really.</span>
            </h2>
            <p className="mt-10 md:mt-14 text-zinc-300 text-lg md:text-xl max-w-3xl mx-auto font-light leading-relaxed">
                The only thing I want in return is our friendship to flourish, and your loyalty.
                You can depend on me. I won't let you down. I'll stay by your side and continue
                to put money behind your success until you achieve what you came for.
            </p>
            <div className="mt-14">
                <Link
                    to="/about"
                    className="inline-flex items-center gap-3 text-white font-heading uppercase tracking-widest text-lg border-b-2 border-[#FF1A1A] pb-2 hover:text-[#FF1A1A] transition-colors"
                    data-testid="promise-read-more"
                >
                    Read the full mission
                    <ArrowRight className="w-5 h-5" />
                </Link>
            </div>
        </div>
    </section>
);

const CTASection = () => (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#FF1A1A]" data-testid="cta-section">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
                <h2 className="font-heading uppercase text-black text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
                    Call me.
                    <br />
                    Let's make it
                    <br />
                    <span className="text-white">real.</span>
                </h2>
            </div>
            <div className="md:col-span-5 flex flex-col gap-5">
                <a
                    href="tel:+14073744236"
                    onClick={handleCall}
                    className="group flex items-center justify-between gap-4 bg-black text-white px-8 py-6 hover:bg-white hover:text-black transition-colors duration-300 cursor-pointer"
                    data-testid="cta-phone"
                >
                    <div className="flex items-center gap-4">
                        <Phone className="w-6 h-6 text-[#FF1A1A] group-hover:text-[#FF1A1A]" />
                        <div>
                            <p className="text-xs uppercase tracking-widest text-zinc-400 group-hover:text-zinc-600">Phone</p>
                            <p className="font-heading text-2xl md:text-3xl uppercase tracking-tight">407-374-4236</p>
                        </div>
                    </div>
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                    href={GMAIL_COMPOSE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleEmail}
                    className="group flex items-center justify-between gap-4 bg-black text-white px-8 py-6 hover:bg-white hover:text-black transition-colors duration-300 cursor-pointer"
                    data-testid="cta-email"
                >
                    <div className="flex items-center gap-4 min-w-0">
                        <Mail className="w-6 h-6 text-[#FF1A1A] flex-shrink-0" />
                        <div className="min-w-0">
                            <p className="text-xs uppercase tracking-widest text-zinc-400 group-hover:text-zinc-600">Email</p>
                            <p className="font-heading text-lg md:text-xl uppercase tracking-tight truncate">
                                tellmeyourdesire666@gmail.com
                            </p>
                        </div>
                    </div>
                    <ArrowRight className="w-6 h-6 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </a>
            </div>
        </div>
    </section>
);

const Home = () => (
    <div className="bg-[#050505] text-white" data-testid="home-page">
        <HeroSection />
        <MarqueeBar />
        <OfferingsSection />
        <PromiseSection />
        <CTASection />
    </div>
);

export default Home;
